// ─── 🏆 Tornei ed eventi ─────────────────────────────────────────────────────
//
// 🗣️ 28/09/2026 — «Notifiche ai soci: testi e frequenza», mockup «Notifiche ai
// soci» approvato (*«mockup approvato, scelte consigliate, procedi»*). La
// Segreteria pubblica i tornei e gli stage — la data, i livelli per cui sono
// pensati, i posti — e ci iscrive i soci. L'assistente li propone a chi ha detto
// sì a «🏆 Tornei ed eventi», del livello giusto e finché ci sono posti.
//
// ⚖️ I livelli si scelgono **a parole**, con la scala del circolo che arriva
// insieme all'elenco: «da Intermedio ad Avanzato». Sotto ci sono i numeri della
// scala, gli stessi della scheda del socio.
// ⛔ Un evento non si cancella: si annulla, e chi era iscritto resta scritto.

import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ETICHETTE_TIPO_EVENTO,
  giornoPerEsteso,
  TIPI_EVENTO,
  type ElencoEventi,
  type ElencoIscritti,
  type Evento,
  type FasciaDiLivello,
  type PersonaTrovata,
  type TipoEvento,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { CampoData } from '../../components/CampoData.js';
import { CampoOra } from '../../components/CampoOra.js';
import { Attesa, Campo, Guasto, Vuoto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica, usaRilettura } from '../impostazioni/comune.js';

const CHIAVE = 'eventi';

/** `2026-10-12` → `12/10/2026`, senza ripassare da un fuso. */
function italiano(giorno: string): string {
  return `${giorno.slice(8, 10)}/${giorno.slice(5, 7)}/${giorno.slice(0, 4)}`;
}

/** «domenica 12 ottobre, alle 9:30» · «dal 12/10 al 13/10». */
function quando(e: Evento): string {
  const giorno = giornoPerEsteso(e.date);
  const ora = e.time ? `, alle ${e.time}` : '';
  if (e.endDate && e.endDate !== e.date) return `${giorno}${ora} → ${italiano(e.endDate)}`;
  return `${giorno}${ora}`;
}

function posti(e: Evento): string {
  if (e.places === null) return `${e.enrolledCount} iscritti · senza limite`;
  return `${e.enrolledCount} su ${e.places} · ${e.placesLeft} liberi`;
}

export function TorneiEdEventi(): React.JSX.Element {
  const modificabile = usaModifica('view_dashboard');
  const elenco = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<ElencoEventi>('GET', '/events'),
  });

  if (elenco.isLoading) return <Attesa cosa="i tornei e gli eventi" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;
  const items = elenco.data?.items ?? [];
  const fasce = elenco.data?.fasce ?? [];
  const oggi = new Date().toLocaleDateString('sv-SE');
  const prossimi = items.filter((e) => (e.endDate ?? e.date) >= oggi);
  const passati = items.filter((e) => (e.endDate ?? e.date) < oggi);

  return (
    <section>
      <header className="testata">
        <h2>🏆 Tornei ed eventi</h2>
        <p className="tenue">
          I tornei e gli stage del circolo. L’assistente li propone ai soci che li hanno chiesti, quando sono
          del loro livello e finché ci sono posti.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      {prossimi.length === 0 ? (
        <Vuoto
          titolo="Il calendario dei tornei è libero"
          perché="Qui sotto si pubblica il prossimo torneo o stage."
        />
      ) : (
        <TabellaEventi items={prossimi} fasce={fasce} modificabile={modificabile} />
      )}

      {modificabile && <NuovoEvento fasce={fasce} />}

      {passati.length > 0 && (
        <details className="riquadro">
          <summary>Passati ({passati.length})</summary>
          <TabellaEventi items={passati} fasce={fasce} modificabile={false} />
        </details>
      )}
    </section>
  );
}

function TabellaEventi({
  items,
  fasce,
  modificabile,
}: {
  items: Evento[];
  fasce: FasciaDiLivello[];
  modificabile: boolean;
}): React.JSX.Element {
  return (
    <div className="tabella-scorre">
      <table>
        <thead>
          <tr>
            <th>Quando</th>
            <th>Tipo</th>
            <th>Titolo</th>
            <th>Livelli</th>
            <th>Posti</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <RigaEvento key={e.id} evento={e} fasce={fasce} modificabile={modificabile} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RigaEvento({
  evento,
  fasce,
  modificabile,
}: {
  evento: Evento;
  fasce: FasciaDiLivello[];
  modificabile: boolean;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  /** ⚠️ Un pannello per volta, in una riga sua sotto: un modulo in una cella sfascia la tabella. */
  const [aperto, setAperto] = useState<'iscritti' | 'modifica' | null>(null);
  const annullato = evento.cancelledAt !== null;
  const annulla = useMutation({
    meta: { fatto: 'evento annullato' },
    mutationFn: () => chiama('POST', `/events/${evento.id}/cancel`),
    onSuccess: rileggi,
  });
  const gira = (p: 'iscritti' | 'modifica') => setAperto((v) => (v === p ? null : p));

  return (
    <>
      <tr className={annullato ? 'tenue' : undefined}>
        <td>{quando(evento)}</td>
        <td>{ETICHETTE_TIPO_EVENTO[evento.kind]}</td>
        <td>
          {evento.title}
          {annullato && <span className="etichetta"> · annullato</span>}
        </td>
        <td>{evento.levelLabel ?? 'aperto a tutti'}</td>
        <td>{posti(evento)}</td>
        <td className="azioni">
          <button
            type="button"
            className="piatto"
            aria-expanded={aperto === 'iscritti'}
            onClick={() => gira('iscritti')}
          >
            Iscritti
          </button>
          {modificabile && !annullato && (
            <>
              <button
                type="button"
                className="piatto"
                aria-expanded={aperto === 'modifica'}
                onClick={() => gira('modifica')}
              >
                Modifica
              </button>
              {/* ⚖️ Annullare non cancella: gli iscritti restano scritti, e il bot smette di proporlo. */}
              <button
                type="button"
                className="piatto pericolo"
                disabled={annulla.isPending}
                onClick={() => {
                  if (window.confirm(`Annullare «${evento.title}»? L’assistente smette di proporlo.`))
                    annulla.mutate();
                }}
              >
                Annulla l’evento
              </button>
            </>
          )}
          <Guasto errore={annulla.error} />
        </td>
      </tr>
      {aperto !== null && (
        <tr className="riga-modifica">
          <td colSpan={6}>
            {aperto === 'modifica' ? (
              <ModificaEvento evento={evento} fasce={fasce} chiudi={() => setAperto(null)} />
            ) : (
              <Iscritti evento={evento} modificabile={modificabile && !annullato} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Il modulo, uguale per il nuovo e per la correzione ───────────────────────

interface Bozza {
  kind: TipoEvento;
  title: string;
  date: string;
  time: string;
  endDate: string;
  levelMin: string;
  levelMax: string;
  places: string;
  description: string;
}

const VUOTA: Bozza = {
  kind: 'torneo',
  title: '',
  date: '',
  time: '',
  endDate: '',
  levelMin: '',
  levelMax: '',
  places: '',
  description: '',
};

function daEvento(e: Evento): Bozza {
  return {
    kind: e.kind,
    title: e.title,
    date: e.date,
    time: e.time ?? '',
    endDate: e.endDate ?? '',
    levelMin: e.levelMin === null ? '' : String(e.levelMin),
    levelMax: e.levelMax === null ? '' : String(e.levelMax),
    places: e.places === null ? '' : String(e.places),
    description: e.description,
  };
}

/** La bozza come la vuole il server: i vuoti diventano `null`. */
function perIlServer(b: Bozza) {
  const posti = b.places.trim() === '' ? null : Number(b.places);
  return {
    kind: b.kind,
    title: b.title.trim(),
    date: b.date,
    time: b.time === '' ? null : b.time,
    endDate: b.endDate === '' || b.endDate === b.date ? null : b.endDate,
    levelMin: b.levelMin === '' ? null : Number(b.levelMin),
    levelMax: b.levelMax === '' ? null : Number(b.levelMax),
    places: posti !== null && Number.isFinite(posti) ? posti : null,
    description: b.description.trim(),
  };
}

/**
 * I livelli, scelti a parole. «Dal livello» vale l'inizio della fascia, «al
 * livello» la sua fine: da Intermedio ad Avanzato è 3,0–4,5.
 * ⚠️ Un numero che non è l'estremo di nessuna fascia (scritto prima che la scala
 * cambiasse) resta scelto e si vede com'è, invece di sparire al primo «Salva».
 */
function SceltaLivello({
  etichetta,
  fasce,
  estremo,
  value,
  onChange,
}: {
  etichetta: string;
  fasce: FasciaDiLivello[];
  estremo: 'da' | 'a';
  value: string;
  onChange(v: string): void;
}): React.JSX.Element {
  const valori = fasce.map((f) => String(f[estremo]));
  return (
    <label className="campo">
      <span>{etichetta}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{estremo === 'da' ? 'dal primo' : 'all’ultimo'}</option>
        {value !== '' && !valori.includes(value) && (
          <option value={value}>{value.replace('.', ',')} (fuori dalla scala)</option>
        )}
        {fasce.map((f) => (
          <option key={f.parola} value={String(f[estremo])}>
            {f.parola}
          </option>
        ))}
      </select>
    </label>
  );
}

function CampiEvento({
  bozza,
  cambia,
  fasce,
}: {
  bozza: Bozza;
  cambia(p: Partial<Bozza>): void;
  fasce: FasciaDiLivello[];
}): React.JSX.Element {
  return (
    <>
      <div className="riga-campi">
        <label className="campo">
          <span>Tipo</span>
          <select value={bozza.kind} onChange={(e) => cambia({ kind: e.target.value as TipoEvento })}>
            {TIPI_EVENTO.map((t) => (
              <option key={t} value={t}>
                {ETICHETTE_TIPO_EVENTO[t]}
              </option>
            ))}
          </select>
        </label>
        <Campo
          etichetta="Titolo"
          required
          maxLength={120}
          value={bozza.title}
          onChange={(e) => cambia({ title: e.target.value })}
        />
      </div>
      <div className="riga-campi">
        <CampoData etichetta="Data" required value={bozza.date} onChange={(v) => cambia({ date: v })} />
        <CampoOra
          etichetta="Ora (vuota = tutto il giorno)"
          value={bozza.time}
          onChange={(v) => cambia({ time: v })}
        />
        <CampoData
          etichetta="Data di fine (vuota = un giorno solo)"
          value={bozza.endDate}
          onChange={(v) => cambia({ endDate: v })}
        />
      </div>
      {fasce.length > 0 ? (
        <div className="riga-campi">
          <SceltaLivello
            etichetta="Dal livello"
            fasce={fasce}
            estremo="da"
            value={bozza.levelMin}
            onChange={(v) => cambia({ levelMin: v })}
          />
          <SceltaLivello
            etichetta="Al livello"
            fasce={fasce}
            estremo="a"
            value={bozza.levelMax}
            onChange={(v) => cambia({ levelMax: v })}
          />
          <Campo
            etichetta="Posti (vuoto = senza limite)"
            inputMode="numeric"
            value={bozza.places}
            onChange={(e) => cambia({ places: e.target.value.replace(/\D/g, '') })}
          />
        </div>
      ) : (
        <>
          <p className="tenue piccolo">
            La scala dei livelli si scrive in «Cosa dice il bot ai soci»: finché c’è solo questa pagina,
            l’evento è aperto a tutti.
          </p>
          <Campo
            etichetta="Posti (vuoto = senza limite)"
            inputMode="numeric"
            value={bozza.places}
            onChange={(e) => cambia({ places: e.target.value.replace(/\D/g, '') })}
          />
        </>
      )}
      <label className="campo">
        <span>Descrizione (la legge il socio: cosa si gioca, quanto costa, come ci si iscrive)</span>
        <textarea
          rows={3}
          maxLength={2000}
          value={bozza.description}
          onChange={(e) => cambia({ description: e.target.value })}
        />
      </label>
    </>
  );
}

function NuovoEvento({ fasce }: { fasce: FasciaDiLivello[] }): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [bozza, setBozza] = useState<Bozza>(VUOTA);
  const crea = useMutation({
    meta: { fatto: 'evento pubblicato' },
    mutationFn: () => chiama<Evento>('POST', '/events', perIlServer(bozza)),
    onSuccess: () => {
      setBozza(VUOTA);
      rileggi();
    },
  });

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        crea.mutate();
      }}
    >
      <h3>Pubblica un torneo o uno stage</h3>
      <CampiEvento bozza={bozza} cambia={(p) => setBozza((b) => ({ ...b, ...p }))} fasce={fasce} />
      <Guasto errore={crea.error} />
      <button type="submit" disabled={crea.isPending || bozza.title.trim() === '' || bozza.date === ''}>
        {crea.isPending ? 'Un attimo…' : 'Pubblica'}
      </button>
    </form>
  );
}

/**
 * Correggere un evento. ⭐ Si manda SOLO quello che è cambiato: ciò che non
 * arriva il server lo lascia com'è.
 */
function ModificaEvento({
  evento,
  fasce,
  chiudi,
}: {
  evento: Evento;
  fasce: FasciaDiLivello[];
  chiudi(): void;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [bozza, setBozza] = useState<Bozza>(() => daEvento(evento));
  const prima = perIlServer(daEvento(evento));
  const dopo = perIlServer(bozza);
  const cambiato = Object.fromEntries(
    (Object.keys(dopo) as Array<keyof typeof dopo>)
      .filter((k) => dopo[k] !== prima[k])
      .map((k) => [k, dopo[k]]),
  );
  const niente = Object.keys(cambiato).length === 0;

  const salva = useMutation({
    meta: { fatto: 'evento salvato' },
    mutationFn: () => chiama<Evento>('PATCH', `/events/${evento.id}`, cambiato),
    onSuccess: () => {
      rileggi();
      chiudi();
    },
  });

  return (
    <form
      className="pannello-fascia"
      onSubmit={(e) => {
        e.preventDefault();
        if (!niente) salva.mutate();
      }}
    >
      <CampiEvento bozza={bozza} cambia={(p) => setBozza((b) => ({ ...b, ...p }))} fasce={fasce} />
      <Guasto errore={salva.error} />
      <div className="azioni">
        <button type="submit" disabled={salva.isPending || niente}>
          {salva.isPending ? 'Un attimo…' : 'Salva'}
        </button>
        <button type="button" className="piatto" onClick={chiudi}>
          Chiudi
        </button>
      </div>
    </form>
  );
}

// ── Chi c'è ──────────────────────────────────────────────────────────────────

function Iscritti({ evento, modificabile }: { evento: Evento; modificabile: boolean }): React.JSX.Element {
  const chiave = [CHIAVE, evento.id, 'iscritti'];
  const rileggiEventi = usaRilettura(CHIAVE);
  const elenco = useQuery({
    queryKey: chiave,
    queryFn: () => chiama<ElencoIscritti>('GET', `/events/${evento.id}/enrolments`),
  });
  const togli = useMutation({
    meta: { fatto: 'iscrizione tolta' },
    mutationFn: (memberId: string) => chiama('DELETE', `/events/${evento.id}/enrolments/${memberId}`),
    onSuccess: rileggiEventi,
  });

  if (elenco.isLoading) return <Attesa cosa="gli iscritti" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;
  const items = elenco.data?.items ?? [];
  const dentro = new Set(items.map((i) => i.memberId));
  const pieno = evento.placesLeft !== null && evento.placesLeft <= 0;

  return (
    <div className="pannello-fascia">
      <p className="piccolo">
        <strong>{posti(evento)}</strong>
      </p>
      {items.length === 0 ? (
        <p className="tenue piccolo">Ancora nessun iscritto: si aggiungono cercandoli qui sotto.</p>
      ) : (
        <ul className="trovati">
          {items.map((i) => (
            <li key={i.memberId}>
              <span>
                {i.displayName}
                {i.levelLabel && <span className="tenue piccolo"> · {i.levelLabel}</span>}
              </span>
              {modificabile && (
                <button
                  type="button"
                  className="piatto pericolo"
                  disabled={togli.isPending}
                  onClick={() => togli.mutate(i.memberId)}
                >
                  Togli
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <Guasto errore={togli.error} />
      {modificabile &&
        (pieno ? (
          <p className="tenue piccolo">
            I posti sono finiti: per aggiungere qualcuno, alza i posti in «Modifica».
          </p>
        ) : (
          <AggiungiIscritto eventoId={evento.id} dentro={dentro} />
        ))}
    </div>
  );
}

/** Si cerca sul server mentre si scrive, come in «Chi entra in campo» del calendario. */
function AggiungiIscritto({
  eventoId,
  dentro,
}: {
  eventoId: string;
  dentro: ReadonlySet<string>;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [testo, setTesto] = useState('');
  const [trovati, setTrovati] = useState<PersonaTrovata[]>([]);
  const iscrivi = useMutation({
    meta: { fatto: 'socio iscritto' },
    mutationFn: (memberId: string) => chiama('POST', `/events/${eventoId}/enrolments`, { memberId }),
    onSuccess: () => {
      setTesto('');
      setTrovati([]);
      rileggi();
    },
  });

  useEffect(() => {
    if (testo.trim().length < 2) {
      setTrovati([]);
      return;
    }
    let annullato = false;
    const t = setTimeout(() => {
      void chiama<{ items: PersonaTrovata[] }>('GET', `/members/lookup?q=${encodeURIComponent(testo.trim())}`)
        .then((r) => {
          if (!annullato) setTrovati(r.items);
        })
        .catch(() => {
          if (!annullato) setTrovati([]);
        });
    }, 250);
    return () => {
      annullato = true;
      clearTimeout(t);
    };
  }, [testo]);

  return (
    <div>
      <label className="campo">
        <span>Iscrivi un socio</span>
        <input value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="cognome o nome" />
      </label>
      {trovati.length > 0 && (
        <ul className="trovati">
          {trovati.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className="piatto"
                disabled={dentro.has(s.id) || iscrivi.isPending}
                onClick={() => iscrivi.mutate(s.id)}
              >
                {s.displayName}
              </button>
              {dentro.has(s.id) && <span className="tenue piccolo">già iscritto</span>}
            </li>
          ))}
        </ul>
      )}
      <Guasto errore={iscrivi.error} />
    </div>
  );
}
